// src/components/admin_panel/AdminInviteLinkAnalytics.js
import React, { useState, useEffect } from "react";

const AdminInviteLinkAnalytics = ({ adminToken }) => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminToken]);

  const fetchAnalytics = async () => {
    if (!adminToken) return;

    setLoading(true);
    setError("");

    try {
      const response = await fetch("/api/admin/invite-links/analytics", {
        headers: { 'Authorization': `Bearer ${adminToken}` }
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setAnalytics(data);
    } catch (err) {
      console.error("Error fetching invite link analytics:", err);
      setError("Failed to load invite link analytics");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div style={{ fontSize: "18px", color: "#666" }}>Loading analytics...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ padding: "40px" }}>
        <div style={{
          background: "#fee2e2",
          border: "1px solid #ef4444",
          borderRadius: "8px",
          padding: "16px",
          color: "#991b1b"
        }}>
          {error}
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div style={{ padding: "40px", textAlign: "center" }}>
        <div style={{ fontSize: "18px", color: "#666" }}>No data available</div>
      </div>
    );
  }

  const conversionRate = analytics.conversion_rate_percent || 0;

  return (
    <div style={{ padding: "20px" }}>
      <h2 style={{ marginBottom: "30px", color: "#111827" }}>
        🔗 Invite Links Analytics
      </h2>

      {/* Summary Cards */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
        gap: "20px",
        marginBottom: "40px"
      }}>
        <div style={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(102, 126, 234, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            Links Created
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {analytics.total_links_created}
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)",
          color: "#111827",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(250, 112, 154, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.7, marginBottom: "8px" }}>
            Clicks
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {analytics.total_clicks}
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
          color: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(240, 147, 251, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            Accounts Created Via Links
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {analytics.total_signups}
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
          color: "#111827",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(67, 233, 123, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.7, marginBottom: "8px" }}>
            Active Subscriptions Via Links
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {analytics.total_active_subscriptions ?? 0}
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)",
          color: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(107, 114, 128, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            Cancelled Subscriptions Via Links
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {analytics.total_cancelled_subscriptions ?? 0}
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #30cfd0 0%, #330867 100%)",
          color: "white",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(48, 207, 208, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.9, marginBottom: "8px" }}>
            Links-to-Account Rate
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {conversionRate.toFixed(1)}%
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #a8edea 0%, #fed6e3 100%)",
          color: "#111827",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(168, 237, 234, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.7, marginBottom: "8px" }}>
            Click-to-Account Rate
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {(analytics.click_to_signup_rate_percent || 0).toFixed(1)}%
          </div>
        </div>

        <div style={{
          background: "linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)",
          color: "#111827",
          padding: "20px",
          borderRadius: "12px",
          boxShadow: "0 4px 15px rgba(255, 154, 158, 0.3)"
        }}>
          <div style={{ fontSize: "14px", opacity: 0.7, marginBottom: "8px" }}>
            Account-to-Subscription Rate
          </div>
          <div style={{ fontSize: "32px", fontWeight: "700" }}>
            {(analytics.signup_to_subscription_rate_percent || 0).toFixed(1)}%
          </div>
        </div>
      </div>

      {/* By A/B Variant: does prefilling the explanatory WhatsApp text
          help or hurt sharing/conversion, vs just sharing the bare link? */}
      <div style={{
        background: "white",
        borderRadius: "12px",
        padding: "24px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        marginBottom: "30px"
      }}>
        <h3 style={{ marginBottom: "20px", color: "#111827" }}>
          Text Included vs Bare Link (A/B)
        </h3>

        {analytics.by_variant && analytics.by_variant.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Variant</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Links Created</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Clicks</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Accounts</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Active Subs</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Cancelled Subs</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Click-to-Account Rate</th>
                </tr>
              </thead>
              <tbody>
                {analytics.by_variant.map((row) => (
                  <tr key={row.label} style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "12px", fontWeight: "500" }}>{row.label}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>{row.links_created}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>{row.clicks}</td>
                    <td style={{ padding: "12px", textAlign: "center", color: "#10b981", fontWeight: "600" }}>{row.signups}</td>
                    <td style={{ padding: "12px", textAlign: "center", color: "#0891b2", fontWeight: "600" }}>{row.active_subscriptions}</td>
                    <td style={{ padding: "12px", textAlign: "center", color: "#6b7280", fontWeight: "600" }}>{row.cancelled_subscriptions}</td>
                    <td style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>{row.click_to_signup_rate_percent.toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
            No invite links created yet
          </div>
        )}
      </div>

      {/* By Theme */}
      <div style={{
        background: "white",
        borderRadius: "12px",
        padding: "24px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        marginBottom: "30px"
      }}>
        <h3 style={{ marginBottom: "20px", color: "#111827" }}>
          By Theme
        </h3>

        {analytics.by_theme && analytics.by_theme.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Theme</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Links Created</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Clicks</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Accounts</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Active Subs</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Cancelled Subs</th>
                </tr>
              </thead>
              <tbody>
                {analytics.by_theme.map((row) => (
                  <tr key={row.theme_name} style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "12px", fontWeight: "500" }}>{row.theme_name}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>{row.links_created}</td>
                    <td style={{ padding: "12px", textAlign: "center" }}>{row.clicks}</td>
                    <td style={{ padding: "12px", textAlign: "center", color: "#10b981", fontWeight: "600" }}>{row.signups}</td>
                    <td style={{ padding: "12px", textAlign: "center", color: "#0891b2", fontWeight: "600" }}>{row.active_subscriptions}</td>
                    <td style={{ padding: "12px", textAlign: "center", color: "#6b7280", fontWeight: "600" }}>{row.cancelled_subscriptions}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
            No invite links created yet
          </div>
        )}
      </div>

      {/* All Links */}
      <div style={{
        background: "white",
        borderRadius: "12px",
        padding: "24px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.1)"
      }}>
        <h3 style={{ marginBottom: "20px", color: "#111827" }}>
          All Invite Links
        </h3>

        {analytics.links && analytics.links.length > 0 ? (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "14px" }}>
              <thead>
                <tr style={{ background: "#f9fafb", borderBottom: "2px solid #e5e7eb" }}>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Sharer</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Theme</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Gathering Date</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Text?</th>
                  <th style={{ padding: "12px", textAlign: "center", fontWeight: "600" }}>Clicks</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Created</th>
                  <th style={{ padding: "12px", textAlign: "left", fontWeight: "600" }}>Accounts Created Via Link</th>
                </tr>
              </thead>
              <tbody>
                {analytics.links.map((link) => (
                  <tr key={link.token} style={{ borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "12px" }}>
                      <div style={{ fontWeight: "500" }}>{link.sharer_name}</div>
                      <div style={{ color: "#666", fontSize: "12px" }}>{link.sharer_email}</div>
                    </td>
                    <td style={{ padding: "12px" }}>{link.theme_name}</td>
                    <td style={{ padding: "12px", color: "#666" }}>
                      {link.gathering_date_time
                        ? new Date(link.gathering_date_time).toLocaleString()
                        : <em>General theme invite</em>}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>
                      {link.share_text_prefilled ? "Yes" : "No"}
                    </td>
                    <td style={{ padding: "12px", textAlign: "center" }}>{link.clicks}</td>
                    <td style={{ padding: "12px", color: "#666" }}>
                      {link.created_at ? new Date(link.created_at).toLocaleString() : "-"}
                    </td>
                    <td style={{ padding: "12px" }}>
                      {link.signups.length > 0 ? (
                        link.signups.map((s, i) => (
                          <div key={i} style={{ color: "#10b981", fontWeight: "600" }}>
                            {s.invited_name} <span style={{ color: "#666", fontWeight: "400" }}>({s.invited_email})</span>
                            {s.subscription_status === "active" && (
                              <span style={{
                                marginLeft: "6px",
                                color: "#0891b2",
                                fontWeight: "600",
                                fontSize: "12px"
                              }}>
                                · Subscribed
                              </span>
                            )}
                            {s.subscription_status === "cancelled" && (
                              <span style={{
                                marginLeft: "6px",
                                color: "#6b7280",
                                fontWeight: "600",
                                fontSize: "12px"
                              }}>
                                · Cancelled
                              </span>
                            )}
                          </div>
                        ))
                      ) : (
                        <span style={{ color: "#9ca3af" }}>No signups yet</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div style={{ padding: "40px", textAlign: "center", color: "#9ca3af" }}>
            No invite links created yet
          </div>
        )}
      </div>

      {/* Refresh Button */}
      <div style={{ marginTop: "30px", textAlign: "center" }}>
        <button
          onClick={fetchAnalytics}
          style={{
            background: "#667eea",
            color: "white",
            border: "none",
            padding: "12px 24px",
            borderRadius: "8px",
            fontSize: "14px",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 2px 8px rgba(102, 126, 234, 0.3)"
          }}
          onMouseEnter={(e) => e.currentTarget.style.background = "#5568d3"}
          onMouseLeave={(e) => e.currentTarget.style.background = "#667eea"}
        >
          🔄 Refresh Analytics
        </button>
      </div>
    </div>
  );
};

export default AdminInviteLinkAnalytics;
